import React from "react";
import { Switch } from "@material-tailwind/react";
import {
  Flag16Regular,
  Flag16Filled,
  Settings20Filled,
} from "@fluentui/react-icons";
import type { Service } from "../../types/types";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { 
  fetchCriticalServices, 
  updateCriticalService 
} from "../../store/slices/criticalServicesSlice";

interface ServicesTabProps {
  services: Service[];
  toggleMaintenance: (id: number) => void;
  toggleGlobalMaintenance: () => void;
  openScriptMode: (id: number) => void;
  setServices: React.Dispatch<React.SetStateAction<Service[]>>;
  podMetricsLoading?: boolean;
  isGlobalMaintenanceActive?: boolean;
  systemType?: string;
}

const ServicesTab: React.FC<ServicesTabProps> = ({
  services,
  toggleMaintenance,
  toggleGlobalMaintenance,
  openScriptMode,
  setServices,
  podMetricsLoading = false,
  isGlobalMaintenanceActive = false,
  systemType = "dev",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Handle toggling critical status
  const toggleCritical = (service: Service) => {
    // Get the service name to use for the API
    const serviceName = service.containerName || service.name.toLowerCase();
    const isCritical = !service.isCritical;    
    // Set loading state for this specific service
    setServices(prev =>
      prev.map(s =>
        s.id === service.id
          ? { ...s, loadingCritical: true }
          : s
      )
    );
    
    const request = {
      cluster: systemType || "dev",
      service_name: serviceName,
      enabled: isCritical,
      priority: 1,
      category: "service",
      description: service.notes || `${service.name} service`,
      created_by: "admin-ui"
    };
    
    // Update the critical service via API
    dispatch(updateCriticalService(request))
      .unwrap()
      .then(() => {
        // Immediately update UI to reflect the change
        setServices(prev =>
          prev.map(s =>
            s.id === service.id
              ? { ...s, isCritical, loadingCritical: false }
              : s
          )
        );
        
        // Refresh critical services to ensure UI is in sync with server
        dispatch(fetchCriticalServices(systemType));
      })
      .catch(() => {
        // Reset loading state on error
        setServices(prev =>
          prev.map(s =>
            s.id === service.id
              ? { ...s, loadingCritical: false }
              : s
          )
        );
      });
  };
  return (
    <>
      <div className="p-2 flex items-center justify-between">
        <div>
          <h2 className="font-inter font-normal text-base text-[#344054]">
            Currently operating
          </h2>
          <h3 className="font-inter font-bold text-2xl text-[#0C111D] mb-2">
            Services
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-roboto font-bold text-sm text-[#344054]">
            Global Maintenance
          </span>
          <Switch
            id="global-maintenance-switch"
            color="blue"
            onChange={toggleGlobalMaintenance}
            checked={isGlobalMaintenanceActive}
            crossOrigin="anonymous"
            onResize={() => {}}
            onResizeCapture={() => {}}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />
        </div>
      </div>
      <div className="box-border bg-white border border-[#E4E7EC] shadow-xl rounded-lg overflow-x-auto">
        {/* Services table */}
        <table className="w-full min-w-max">
          <thead>
            <tr className="text-gray-600 text-sm bg-gray-50">
              <th className="text-left py-2 px-4 font-inter font-semibold text-[#475467] border-b border-[#E4E7EC]">
                Services Names
              </th>
              <th className="text-left py-2 px-4 font-inter font-semibold text-[#475467] border-b border-[#E4E7EC]">
                Notes
              </th>
              <th className="text-left py-2 px-4 w-24 font-inter font-semibold text-[#475467] border-b border-[#E4E7EC]">
                Status
              </th>
              <th className="text-center py-2 px-4 w-28 font-inter font-semibold text-[#475467] border-b border-[#E4E7EC]">
                Maintenance
              </th>
              <th className="text-center py-2 px-4 w-20 font-inter font-semibold text-[#475467] border-b border-[#E4E7EC]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr
                key={service.id}
                className="border-b border-[#E4E7EC] hover:bg-gray-50"
              >
                <td className="px-4">
                  <div className="flex items-center">
                    <div
                      className="mr-5 cursor-pointer"
                      onClick={() => toggleCritical(service)}
                    >
                      {/* Track loading state per service ID */}
                      {service.loadingCritical ? (
                        <span className="inline-block w-4 h-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></span>
                      ) : service.isCritical ? (
                        <Flag16Filled className="text-red-600" style={{ color: "#e81818" }} />
                      ) : (
                        <Flag16Regular />
                      )}
                    </div>
                    <div className="font-inter text-base text-[#344054]">
                      {service.name}
                    </div>
                  </div>
                </td>
                <td className="px-4 font-inter text-base text-[#344054]">
                  {service.notes}
                </td>
                <td className="px-4 w-24">
                  {(() => {
                    // If global maintenance is active or service is in maintenance mode,
                    // show maintenance status regardless of pod health
                    // let status = isGlobalMaintenanceActive || service.maintenance ? "Maintenance" : service.status;
                    let status = service.status;
                    let statusColor = "text-green-500";
                    
                    // Apply color based on status
                    if (status === "Maintenance") {
                      statusColor = "text-blue-500";
                    } else if (status === "Failed") {
                      statusColor = "text-red-500";
                    } else if (status === "Pending") {
                      statusColor = "text-yellow-500";
                    } else if (status === "Running") {
                      statusColor = "text-green-500";
                    }
                    
                    return (
                      <span className={`font-inter font-normal text-sm ${statusColor}`}>
                        {status === "Running" ? "Operational" : status === "Failed" ? "Outage" : status === "Pending" ? "Degraded" : status === "Maintenance" ? "Maintenance" : status}
                      </span>
                    );
                  })()}
                  {podMetricsLoading && (
                    <div className="text-xs text-gray-400">Loading...</div>
                  )}
                </td>
                <td className="px-4 w-28">
                  <div className="flex justify-center">
                    <Switch
                      id={`service-${service.id}-switch`}
                      color="blue"
                      onChange={() => toggleMaintenance(service.id)}
                      checked={isGlobalMaintenanceActive || service.maintenance}
                      crossOrigin="anonymous"
                      onResize={() => {}}
                      onResizeCapture={() => {}}
                      onPointerEnterCapture={() => {}}
                      onPointerLeaveCapture={() => {}}
                    />
                  </div>
                </td>
                <td className="px-4 py-2 text-center w-20">
                  <Settings20Filled
                    className="cursor-pointer"
                    onClick={() => openScriptMode(service.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} className="px-4 py-4"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

export default ServicesTab;
